import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("reset") || "";
  const [tokenOk, setTokenOk] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setTokenOk(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/verify-reset-token`, { params: { token } });
        if (!cancelled) setTokenOk(Boolean(data.valid));
      } catch {
        if (!cancelled) setTokenOk(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      setErr("The two passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw }, { withCredentials: true });
      setDone(true);
    } catch (error) {
      setErr(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
    setBusy(false);
  };

  const backLink = (
    <a
      href="/inbox"
      className="mt-4 block text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
    >
      Back to sign in
    </a>
  );

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-ink px-6 py-12 font-body text-white"
      data-testid="reset-page"
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/5 bg-surface p-8 text-center">
        <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-hover text-accentlight">
          {done ? <CheckCircle2 className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
        </span>
        {done ? (
          <>
            <h1 className="font-display text-3xl tracking-wider">ALL SET</h1>
            <p className="mt-2 text-sm text-white/50">Your new password is ready to use.</p>
            <a
              href="/inbox"
              data-testid="reset-success-link"
              className="mt-6 block w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110"
            >
              Open your inbox
            </a>
          </>
        ) : tokenOk === null ? (
          <>
            <h1 className="font-display text-3xl tracking-wider">ONE MOMENT…</h1>
            <p className="mt-2 text-sm text-white/50">Checking your reset link.</p>
          </>
        ) : tokenOk === false ? (
          <>
            <h1 className="font-display text-3xl tracking-wider">NEW PASSWORD</h1>
            <p className="mt-2 text-sm text-white/50">Choose a new password for your Owner Inbox.</p>
            <p
              className="mt-6 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
              data-testid="reset-invalid-link"
            >
              This reset link is invalid, expired, or already used — request a fresh one from the inbox sign-in page.
            </p>
            {backLink}
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl tracking-wider">NEW PASSWORD</h1>
            <p className="mt-2 text-sm text-white/50">Choose a new password for your Owner Inbox.</p>
            <form onSubmit={submit} data-testid="reset-form">
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="New password (min 8 characters)"
                required
                autoComplete="new-password"
                data-testid="reset-password-input"
                className="mt-6 w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
              />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat new password"
                required
                autoComplete="new-password"
                data-testid="reset-confirm-input"
                className="mt-3 w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-accentlight"
              />
              {err && (
                <p className="mt-3 text-xs text-red-300" data-testid="reset-error">
                  {err}
                </p>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit-button"
                className="mt-4 w-full rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
              >
                Save new password
              </button>
            </form>
            {backLink}
          </>
        )}
      </div>
    </div>
  );
}